import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Accessibility, Upload, FileText, Download, ChevronRight, ChevronDown,
  Check, X, Loader2, ExternalLink, Eye, CheckCircle2, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status colors
const STATUS_COLORS = {
  accessible: '#059669',
  issues_found: '#d97706',
  unknown: '#6b7280',
  no_data: '#6b7280',
};

// Badge component
function Badge({ children, color = '#6b7280', size = 'normal' }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: size === 'small' ? '2px 6px' : '3px 10px',
        borderRadius: 999,
        fontSize: size === 'small' ? 10 : 11,
        fontWeight: 700,
        letterSpacing: '0.04em',
        background: color + '18',
        color,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </span>
  );
}

// Card component
function Card({ children, className = '', onClick, style = {} }) {
  return (
    <div
      className={`threebie-card ${className} ${onClick ? 'clickable' : ''}`}
      onClick={onClick}
      style={style}
    >
      {children}
    </div>
  );
}

// Upload Section
function UploadSection({ onUploadComplete }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleUpload = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('report_label', file.name);

    try {
      // SECURITY: Use cookies only (credentials: 'include'), no localStorage
      const response = await fetch(`${API_URL}/api/accessibility/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Upload failed');
      }

      toast.success(`Report parsed: ${data.total_elements} elements tested, ${data.passed_elements} passed`);
      onUploadComplete?.();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  return (
    <Card className="upload-section">
      <h3><Upload size={18} /> Upload Accessibility Report</h3>
      <div
        className={`upload-dropzone ${dragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid="accessibility-upload-dropzone"
      >
        {uploading ? (
          <>
            <Loader2 className="spin" size={32} />
            <p>Parsing report...</p>
          </>
        ) : (
          <>
            <Accessibility size={32} />
            <p>Drop accessibility audit PDF here or click to browse</p>
            <span>Supports accessiBe scan reports</span>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
              data-testid="accessibility-file-input"
            />
          </>
        )}
      </div>
    </Card>
  );
}

// Report Card with expandable elements
function ReportCard({ report, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [elements, setElements] = useState([]);
  const [loadingElements, setLoadingElements] = useState(false);

  const loadElements = async () => {
    if (elements.length > 0) return;
    setLoadingElements(true);
    try {
      const response = await fetch(`${API_URL}/api/accessibility/reports/${report.report_id}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setElements(data.elements || []);
      }
    } catch (error) {
      console.error('Failed to load elements:', error);
    } finally {
      setLoadingElements(false);
    }
  };

  const handleExpand = () => {
    if (!expanded) loadElements();
    setExpanded(!expanded);
  };

  const handleDownload = () => {
    // Downloads use query token (logged by server) - this is the only exception
    const cookies = document.cookie.split(';').reduce((acc, c) => {
      const [key, val] = c.trim().split('=');
      acc[key] = val;
      return acc;
    }, {});
    const token = cookies['session_token'] || '';
    window.open(`${API_URL}/api/accessibility/reports/${report.report_id}/download?token=${token}`, '_blank');
  };

  const scorePct = report.total_elements > 0 
    ? Math.round((report.passed_elements / report.total_elements) * 100) 
    : 0;

  return (
    <Card className="report-card-a11y">
      <div className="report-header" onClick={handleExpand}>
        <div className="report-info">
          <div className="report-title">
            <Badge color={STATUS_COLORS[report.overall_status] || '#6b7280'}>
              {report.overall_status === 'accessible' ? 'Accessible' : 
               report.overall_status === 'issues_found' ? 'Issues Found' : 'Unknown'}
            </Badge>
            <h4>{report.label || report.filename}</h4>
          </div>
          <div className="report-meta">
            <span>{report.scanner || 'Unknown Scanner'}</span>
            <span>•</span>
            <span>{report.scan_date || 'Unknown date'}</span>
            <span>•</span>
            <span>{report.target_url || 'Unknown target'}</span>
          </div>
        </div>
        <div className="report-summary">
          <div className="a11y-score" style={{ color: scorePct >= 90 ? '#059669' : scorePct >= 70 ? '#d97706' : '#dc2626' }}>
            {scorePct}%
          </div>
          <div className="a11y-counts">
            <span className="passed"><CheckCircle2 size={14} /> {report.passed_elements}</span>
            <span className="failed"><AlertTriangle size={14} /> {report.failed_elements}</span>
          </div>
          <span className="expand-icon">
            {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="report-details">
          <div className="report-actions">
            <button onClick={handleDownload} className="btn-secondary" data-testid={`download-${report.report_id}`}>
              <Download size={16} /> Download PDF
            </button>
            {report.target_url && (
              <a href={report.target_url.startsWith('http') ? report.target_url : `https://${report.target_url}`} 
                 target="_blank" rel="noopener noreferrer" className="btn-secondary">
                <ExternalLink size={16} /> View Site
              </a>
            )}
          </div>

          {loadingElements ? (
            <div className="loading-elements">
              <Loader2 className="spin" size={20} />
              <span>Loading element results...</span>
            </div>
          ) : elements.length > 0 ? (
            <div className="elements-list">
              <h5>Element Test Results ({elements.length})</h5>
              <div className="elements-grid">
                {elements.map((elem, i) => (
                  <div key={i} className={`element-item ${elem.passed ? 'passed' : 'failed'}`}>
                    <div className="element-status">
                      {elem.passed ? <Check size={14} color="#059669" /> : <X size={14} color="#dc2626" />}
                    </div>
                    <div className="element-details">
                      <span className="element-num">#{elem.element_number}</span>
                      <span className="element-desc">{elem.description}</span>
                      {elem.score !== undefined && (
                        <span className="element-score">Score: {elem.score}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="no-elements">No individual element results parsed from this report.</p>
          )}
        </div>
      )}
    </Card>
  );
}

// Main Accessibility Page
function AccessibilityPage() {
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [reportsRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/accessibility/reports`, { credentials: 'include' }),
        fetch(`${API_URL}/api/accessibility/summary`, { credentials: 'include' }),
      ]);

      if (reportsRes.ok) {
        const data = await reportsRes.json();
        setReports(data.reports || []);
      }

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Failed to fetch accessibility data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="accessibility-page loading">
        <Loader2 className="spin" size={32} />
        <p>Loading accessibility audits...</p>
      </div>
    );
  }

  return (
    <div className="accessibility-page" data-testid="accessibility-page">
      <div className="page-header">
        <div>
          <h1><Accessibility size={24} /> Accessibility Audits</h1>
          <p>Track WCAG compliance and accessibility scan results</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && summary.total_reports > 0 && (
        <div className="a11y-summary">
          <Card className="summary-card">
            <div className="summary-icon" style={{ background: STATUS_COLORS[summary.current_status] + '20' }}>
              {summary.current_status === 'accessible' ? (
                <CheckCircle2 size={24} color={STATUS_COLORS.accessible} />
              ) : summary.current_status === 'issues_found' ? (
                <AlertTriangle size={24} color={STATUS_COLORS.issues_found} />
              ) : (
                <Accessibility size={24} color={STATUS_COLORS.unknown} />
              )}
            </div>
            <div className="summary-info">
              <span className="summary-value" style={{ color: STATUS_COLORS[summary.current_status] }}>
                {summary.current_status === 'accessible' ? 'Accessible' : 
                 summary.current_status === 'issues_found' ? 'Issues Found' : 'Unknown'}
              </span>
              <span className="summary-label">Current Status</span>
            </div>
          </Card>
          <Card className="summary-card">
            <div className="summary-icon">
              <Eye size={24} color="#FF00BE" />
            </div>
            <div className="summary-info">
              <span className="summary-value">{summary.latest_results?.score_pct || 0}%</span>
              <span className="summary-label">Accessibility Score</span>
            </div>
          </Card>
          <Card className="summary-card">
            <div className="summary-icon">
              <CheckCircle2 size={24} color="#059669" />
            </div>
            <div className="summary-info">
              <span className="summary-value">{summary.latest_results?.passed || 0}</span>
              <span className="summary-label">Elements Passed</span>
            </div>
          </Card>
          <Card className="summary-card">
            <div className="summary-icon">
              <AlertTriangle size={24} color="#dc2626" />
            </div>
            <div className="summary-info">
              <span className="summary-value">{summary.latest_results?.failed || 0}</span>
              <span className="summary-label">Elements Failed</span>
            </div>
          </Card>
        </div>
      )}

      {/* Upload Section */}
      <UploadSection onUploadComplete={fetchData} />

      {/* Reports List */}
      <h2 className="section-title">Uploaded Reports</h2>
      {reports.length === 0 ? (
        <Card className="empty-state">
          <Accessibility size={48} color="#6b7280" />
          <h3>No accessibility reports uploaded yet</h3>
          <p>Upload your first accessibility audit report above to get started.</p>
        </Card>
      ) : (
        <div className="reports-list-a11y">
          {reports.map((report) => (
            <ReportCard
              key={report.report_id}
              report={report}
              onRefresh={fetchData}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default AccessibilityPage;
